export const DEFAULT_MAIN_VIEW_REVEAL_TOP_RESERVE = 24;
export const DEFAULT_MAIN_VIEW_REVEAL_BOTTOM_RESERVE = 24;

export function getMainViewScrollableNode(scrollRef) {
  const refCurrent = scrollRef?.current;
  if (!refCurrent) {
    return null;
  }

  const scrollableNode = refCurrent.getScrollableNode?.();
  if (scrollableNode) {
    return scrollableNode;
  }

  if (refCurrent.scrollTop !== undefined) {
    return refCurrent;
  }

  const firstChild = refCurrent.children?.[0];
  if (firstChild && firstChild.scrollTop !== undefined) {
    return firstChild;
  }

  return null;
}

export function getMainViewViewportHeight(scrollNode) {
  if (typeof window !== "undefined") {
    const visualViewportHeight = window.visualViewport?.height;
    if (Number.isFinite(visualViewportHeight) && visualViewportHeight > 0) {
      return visualViewportHeight;
    }

    if (typeof window.innerHeight === "number" && window.innerHeight > 0) {
      return window.innerHeight;
    }
  }

  return Number(scrollNode?.clientHeight ?? 0);
}

export function resolveMainViewRevealScrollTop({
  currentScrollTop,
  scrollHeight,
  clientHeight,
  containerRect,
  targetRect,
  topReserve = DEFAULT_MAIN_VIEW_REVEAL_TOP_RESERVE,
  bottomReserve = DEFAULT_MAIN_VIEW_REVEAL_BOTTOM_RESERVE,
  viewportHeight,
}) {
  if (
    !Number.isFinite(currentScrollTop) ||
    !Number.isFinite(scrollHeight) ||
    !Number.isFinite(clientHeight) ||
    !containerRect ||
    !targetRect
  ) {
    return null;
  }

  const resolvedViewportHeight =
    Number.isFinite(viewportHeight) && viewportHeight > 0
      ? viewportHeight
      : Number(containerRect.bottom ?? 0);
  const safeTop = Math.max(Number(containerRect.top ?? 0), 0) + topReserve;
  const safeBottom =
    Math.min(Number(containerRect.bottom ?? 0), resolvedViewportHeight) -
    bottomReserve;
  const availableHeight = safeBottom - safeTop;

  if (!Number.isFinite(availableHeight) || availableHeight <= 0) {
    return null;
  }

  const targetTop = Number(targetRect.top ?? 0);
  const targetBottom = Number(targetRect.bottom ?? 0);
  const targetHeight = Number(targetRect.height ?? targetBottom - targetTop);

  if (!Number.isFinite(targetHeight) || targetHeight <= 0) {
    return null;
  }

  let desiredTop = null;

  if (targetHeight <= availableHeight) {
    if (targetTop < safeTop) {
      desiredTop = safeTop;
    } else if (targetBottom > safeBottom) {
      desiredTop = safeBottom - targetHeight;
    }
  } else if (targetBottom > safeBottom) {
    desiredTop = safeBottom - targetHeight;
  } else if (targetTop > safeTop) {
    desiredTop = safeTop;
  }

  if (!Number.isFinite(desiredTop)) {
    return currentScrollTop;
  }

  const unclampedScrollTop = currentScrollTop + (targetTop - desiredTop);
  const maxScrollTop = Math.max(0, scrollHeight - clientHeight);
  return Math.min(Math.max(unclampedScrollTop, 0), maxScrollTop);
}

export function revealInMainView({
  scrollRef,
  targetNode,
  topReserve = DEFAULT_MAIN_VIEW_REVEAL_TOP_RESERVE,
  bottomReserve = DEFAULT_MAIN_VIEW_REVEAL_BOTTOM_RESERVE,
  animated = true,
}) {
  const scrollViewRef = scrollRef?.current;
  if (
    typeof window === "undefined" ||
    !targetNode?.getBoundingClientRect ||
    !scrollViewRef
  ) {
    return false;
  }

  const scrollNode = getMainViewScrollableNode(scrollRef);
  if (!scrollNode?.getBoundingClientRect) {
    return false;
  }

  const targetRect = targetNode.getBoundingClientRect();
  if (!Number.isFinite(targetRect?.height) || targetRect.height <= 0) {
    return false;
  }

  const containerRect = scrollNode.getBoundingClientRect();
  const currentScrollTop = Number(scrollNode.scrollTop ?? 0);
  const clientHeight = Number(scrollNode.clientHeight ?? 0);
  const scrollHeight = Number(scrollNode.scrollHeight ?? 0);
  const viewportHeight = getMainViewViewportHeight(scrollNode);
  const nextScrollTop = resolveMainViewRevealScrollTop({
    currentScrollTop,
    scrollHeight,
    clientHeight,
    containerRect,
    targetRect,
    topReserve,
    bottomReserve,
    viewportHeight,
  });

  if (!Number.isFinite(nextScrollTop)) {
    return false;
  }

  if (Math.abs(nextScrollTop - currentScrollTop) < 1) {
    return false;
  }

  if (typeof scrollViewRef.scrollTo === "function") {
    scrollViewRef.scrollTo({
      y: nextScrollTop,
      animated,
    });
  } else if (typeof scrollNode.scrollTo === "function") {
    scrollNode.scrollTo({
      top: nextScrollTop,
      behavior: animated ? "smooth" : "auto",
    });
  } else {
    scrollNode.scrollTop = nextScrollTop;
  }

  return true;
}
